import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { replyView } from '../components/OnboardingReply/hooks/useOnboardingReply';

// Onboarding stages the home composer renders instead of the prompt (useOnboarding).
const ONBOARDING_FLOW = ['loading', 'handle', 'confirm', 'analyzing', 'questioning', 'memory', 'completing'];
// The conversation keeps one surface: its steps slide inside it (OnboardingReply).
const CONVERSATION = ['analyzing', 'questioning', 'memory', 'completing'];

export default function useComposer(s, compact) {
  const { entry, updateEntry, setReply, onboarding } = s;
  const action = compact && s.run?.status === 'waiting_user' ? s.run.pending_actions.find(item => item.status === 'open') : null;
  const flow = !compact && ONBOARDING_FLOW.includes(onboarding?.stage) ? onboarding.stage : null;
  const conversation = !action && CONVERSATION.includes(flow);
  const [step, setStep] = useState(null);
  const [height, setHeight] = useState(null);
  const content = useRef(null);
  // The view of the current step (a decision, a question, the memory question):
  // options or input. It starts where the step opens and is kept per step.
  const stepId = action ? action.id : flow === 'questioning' ? onboarding.question?.id : flow;
  const opening = action ? 'choices' : flow === 'questioning' ? replyView(onboarding.question, onboarding.record?.answers)
    : flow === 'memory' || flow === 'completing' ? 'options' : null;
  const view = step && step.id === stepId ? step.view : opening;
  const mode = action ? `${action.id}-${view}` : flow ? `onboarding-${flow}-${onboarding.question?.id || ''}-${view || ''}` : 'prompt';
  const surface = conversation ? 'onboarding-conversation' : mode;
  // Options narrow the surface into a picker; the input takes the full width again.
  const picker = action ? view !== 'story' : conversation && view === 'options';
  // The first onboarding control (and the one after loading) does not grab focus: on a
  // phone that would open the keyboard over the page before the visitor reads it.
  const previousMode = useRef(flow ? null : 'prompt');
  const reduced = useReducedMotion();
  const needsFormat = !compact && !flow && entry.formatSelected !== true;
  const previouslyNeededFormat = useRef(needsFormat);
  useEffect(() => {
    if (previouslyNeededFormat.current && !needsFormat) content.current?.querySelector('textarea')?.focus({ preventScroll: true });
    previouslyNeededFormat.current = needsFormat;
  }, [needsFormat]);
  useEffect(() => {
    const node = content.current;
    if (!node) return;
    const measure = () => setHeight(Math.ceil(node.getBoundingClientRect().height));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    // Transfer keyboard focus with the changing control, without moving the chat.
    // A step on its way out is inert (ReplyStep), so it never takes the focus.
    if (previousMode.current && !previousMode.current.startsWith('onboarding-loading') && mode !== previousMode.current) {
      const live = selector => [...node.querySelectorAll(selector)].find(element => !element.closest('[inert]'));
      const target = action && view !== 'story' ? node.querySelector('section')
        : live('textarea:not(:disabled), input:not(:disabled)') || live('[data-autofocus]:not(:disabled)');
      target?.focus({ preventScroll: true });
    }
    previousMode.current = mode;
    return () => observer.disconnect();
  }, [mode, action, view]);
  const contentRef = node => { if (node) content.current = node; };
  const setView = next => setStep({ id: stepId, view: next });
  // The chat reply is a draft (useDraft): the field subscribes to it, so typing renders
  // the field alone. The home prompt is the entry, kept across sign-in.
  const draft = compact ? s.replyDraft : null;
  const value = compact ? '' : entry.prompt;
  const setValue = text => compact ? setReply(text) : updateEntry({ prompt: text });
  const submit = () => { if (!needsFormat) return compact ? s.send() : s.create(); };
  // The home prompt, its format chosen. A chosen topic is in the prompt as a chip, so the
  // others step aside until it is removed.
  const prompting = !compact && !flow && entry.formatSelected === true;
  const showTopics = prompting && !s.topic;
  // Refine prompts only once a generation has finished and left a script (or an art) to refine.
  const showRefine = compact && (s.art?.enabled ? !!s.art.latest : !!s.revision) && !s.active;
  // An art (single post, carousel) opens its template gallery from the toolbar.
  return { action, flow, view, setView, mode, surface, picker, height, reduced, contentRef, draft, value, setValue, submit, showTopics, showRefine, needsFormat };
}
